import { useInterval } from "@mantine/hooks";
import cx from "classnames";
import {
  type CSSProperties,
  Children,
  type ReactNode,
  forwardRef,
  useEffect,
  useState,
} from "react";
import { t } from "ttag";

import { LoadingSpinner } from "metabase/common/components/LoadingSpinner";
import CS from "metabase/css/core/index.css";

// A screen reader only announces a *change* to an already-registered live
// region -- if the region and its text both appear in the same DOM commit
// (the common case here: this component is very often already loading on
// its very first render), most screen readers won't announce anything,
// since there's no "before" state for them to have observed. Setting the
// text from a plain effect (rather than inline during render) defers it
// until after React's initial commit -- in a real browser that's after
// paint, giving assistive tech a chance to register the (empty) region
// first; deliberately not requestAnimationFrame, which -- unlike a plain
// effect -- React Testing Library's `act()` doesn't flush synchronously,
// so it would silently break every existing synchronous `getByText`
// assertion against this component's loading/error text. This keeps a
// single source of truth for the text (avoiding a second, hidden copy
// that would make every such assertion ambiguous instead).
function useDeferredText(text: string): string {
  const [deferred, setDeferred] = useState("");

  useEffect(() => {
    setDeferred(text);
  }, [text]);

  return deferred;
}

export interface LoadingAndErrorWrapperProps {
  loading?: boolean;
  error?: any;
  noBackground?: boolean;
  noWrapper?: boolean;
  showSpinner?: boolean;
  getLoadingMessages?: () => string[];
  messageInterval?: number;
  renderError?: (message?: string) => ReactNode;
  style?: CSSProperties;
  className?: string;
  "data-testid"?: string;

  children?: ReactNode | (() => ReactNode);
}

export const LoadingAndErrorWrapper = forwardRef<
  HTMLDivElement,
  LoadingAndErrorWrapperProps
>(function LoadingAndErrorWrapperInner(
  {
    loading = false,
    error,
    noBackground = true,
    noWrapper = false,
    showSpinner = true,
    getLoadingMessages = () => [t`Loading...`],
    messageInterval = 6000,
    renderError: renderCustomError,
    style,
    className,
    "data-testid": testId,
    children,
  },
  ref,
) {
  const [messageIndex, setMessageIndex] = useState(0);

  const loadingInterval = () => {
    if (loading) {
      setMessageIndex(
        (oldIndex) => (oldIndex + 1) % getLoadingMessages().length,
      );
    }
  };
  const interval = useInterval(loadingInterval, messageInterval);
  useEffect(() => {
    if (getLoadingMessages().length > 1) {
      interval.start();
    }
    return interval.stop;
  });

  function getErrorMessage() {
    let errorMessage =
      // NOTE Atte Keinänen 5/10/17 Dashboard API endpoint returns the error as JSON with `message` field
      error &&
      ((error.data?.message ? error.data.message : error.data) ||
        error.statusText ||
        error.message ||
        error);

    if (!errorMessage || typeof errorMessage !== "string") {
      errorMessage = t`An error occurred`;
    }
    return errorMessage;
  }

  function renderError(contentClassName: string, errorMessage: string) {
    if (renderCustomError) {
      return <div className={CS.py4}>{renderCustomError(errorMessage)}</div>;
    }

    return (
      <div className={contentClassName}>
        <h2 className={cx(CS.textNormal, CS.textLight, CS.ieWrapContentFix)}>
          {errorMessage}
        </h2>
      </div>
    );
  }

  function getChildren(child = children): ReactNode {
    if (Array.isArray(child)) {
      return child.map(getChildren);
    } else if (typeof child === "function") {
      return child();
    } else {
      return child;
    }
  }

  const contentClassName = cx(
    CS.wrapper,
    CS.py4,
    CS.textBrand,
    CS.textCentered,
    CS.flexFull,
    CS.flex,
    CS.flexColumn,
    CS.layoutCentered,
    { [CS.bgWhite]: !noBackground },
  );

  const deferredErrorMessage = useDeferredText(error ? getErrorMessage() : "");
  const deferredLoadingMessage = useDeferredText(
    loading ? getLoadingMessages()[messageIndex] : "",
  );

  if (noWrapper && !error && !loading) {
    const children = getChildren();
    // special case for loading wrapper with null/undefined child
    if (children == null) {
      return null;
    }
    return Children.only(children);
  }
  return (
    <div className={className} style={style} data-testid={testId} ref={ref}>
      {error ? (
        <div role="alert">
          {renderError(contentClassName, deferredErrorMessage)}
        </div>
      ) : loading ? (
        <div className={contentClassName} role="status" aria-live="polite">
          {showSpinner && <LoadingSpinner />}
          <h2 className={cx(CS.textNormal, CS.textLight, CS.mt1)}>
            {deferredLoadingMessage}
          </h2>
        </div>
      ) : (
        getChildren()
      )}
    </div>
  );
});
